import React from 'react';
import { useField } from 'react-final-form';
import { Box, Select, Text } from '@chakra-ui/react';
import { NIL } from 'uuid';
import FieldLabel from './FieldLabel';
import useContact from '../../hooks/useContact';
import useProjects from '../../hooks/useProjects';
import useUsers from '../../hooks/useUsers';
import { Project } from '../../models/project';
import { CustomerStaffMember } from '../../models/contactInfo';

// item 219/485 — pick the CUSTOMER's project manager / account rep for this proposal, from the
// customer's CRM staff roster (item 206). This person now DRIVES staff-level Sales PO: at
// invoice finalize the rep linked to this staff member earns the Sales PO commission (else the
// customer-level inside rep). The customer is resolved from the proposal's customerId, or — for
// a brand-new proposal — from the project just selected. All hooks at top level (rules-of-hooks).
const CustomerProjectManagerField: React.FC = () => {
  const { data: projects } = useProjects();
  const { data: usersData } = useUsers();
  const customerIdField = useField('customerId', { subscription: { value: true } });
  const projectIdField = useField('projectId', { subscription: { value: true } });
  const pmField = useField<string>('customerProjectManagerId');

  const projectCustomerId = (projects?.index ?? [])
    .find((p: Project) => p.id === projectIdField.input.value)?.customerId;
  const customerId = customerIdField.input.value || projectCustomerId || '';

  const { data: customer } = useContact({ id: customerId || NIL });
  const staff: CustomerStaffMember[] = customer?.staff ?? [];

  // item 485 — show who this routes commissions to: Sales PO = customer-level inside rep if set,
  // else the selected staff person's linked rep; Sales I/O = the customer's outside rep (always).
  const userName = (id?: string | null) => (id
    ? (usersData?.index ?? []).find((u) => u.id === id)?.name : undefined);
  const selectedStaff = staff.find((s) => s.id === pmField.input.value);
  const poRepName = userName((customer as any)?.insideSalesRepUserId || selectedStaff?.userId);
  const ioRepName = userName((customer as any)?.outsideSalesRepUserId);

  return (
    // item 332 — this used to render a bare fragment: inside a row Stack the label
    // became a SIBLING flex item and sat BESIDE the select (Chad's screenshot). One
    // full-width Box + the shared FieldLabel puts it back in the normal field rhythm.
    <Box w="full">
      {/* item 833/C2 (Chad) — LABEL ONLY: "Customer Project Manager" reads "Salesperson" now. The
          field name (customerProjectManagerId), the staff roster, and the commission routing below
          are UNCHANGED — commissions are wired to this field. */}
      <FieldLabel>Salesperson</FieldLabel>
      <Select
        {...pmField.input}
        value={pmField.input.value || ''}
        placeholder="— Select salesperson —"
        isDisabled={!customerId || staff.length === 0}
      >
        {staff.map((s) => (
          <option key={s.id} value={s.id}>
            {s.role ? `${s.name} — ${s.role}` : s.name}
          </option>
        ))}
      </Select>
      {customerId && staff.length === 0 && (
        <Text fontSize="xs" mt={1} opacity={0.6}>
          This customer has no staff yet — add them on the customer&apos;s CRM record.
        </Text>
      )}
      {customerId && (poRepName || ioRepName) && (
        <Text fontSize="xs" mt={1} opacity={0.75}>
          {`Commissions route to — Sales PO: ${poRepName ?? '—'} · Sales I/O: ${ioRepName ?? '—'}`}
        </Text>
      )}
    </Box>
  );
};

export default CustomerProjectManagerField;
